import React from 'react';
import { motion } from 'framer-motion';
import { MaskText, Reveal, SectionLabel } from './ui/Reveal';
import { MagneticButton } from './ui/MagneticButton';
import { images } from '../data/site';
import { usePrefersReducedMotion } from '../hooks/useMedia';
const pillars = [{
  title: 'The mark',
  copy: 'A logo engineered for every size, surface and situation — not a decoration that only works once.'
}, {
  title: 'The system',
  copy: 'Type, colour, layout, imagery and motion working as one recognisable language.'
}, {
  title: 'The rules',
  copy: 'Guidelines that keep the brand consistent long after handover, in anyone’s hands.'
}];

/** Primary specialization: brand identity. One of the strongest sections on the site. */
export function Specialization() {
  const reduced = usePrefersReducedMotion();
  return <section className="relative overflow-hidden bg-[#030BA6] py-20 text-white sm:py-28" aria-label="Brand identity specialization" data-id="element-521" data-mp="2u725k98d96mc">
      <motion.div aria-hidden="true" className="pointer-events-none absolute -right-32 -top-24 h-[28rem] w-[28rem] rounded-full border border-white/15" animate={reduced ? undefined : {
      scale: [1, 1.08, 1]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-522" data-mp="2vtcdqx54rn9s" />

      <div className="relative mx-auto w-full max-w-container px-5 sm:px-8" data-id="element-523" data-mp="2h35333ld78jn">
        <SectionLabel className="text-white/70" data-id="element-524" data-mp="2c5ljdo5we3kb">Primary specialization</SectionLabel>

        <h2 className="mt-6 max-w-5xl font-display text-[10vw] font-extrabold uppercase leading-[0.86] tracking-tightest sm:text-[7vw] lg:text-[5.4vw]" data-id="element-525" data-mp="283qek9ord81s">
          <MaskText text="We Don’t Just Design Logos.|We Build Identities." data-id="element-526" data-mp="234nmdptvmwb8" />
        </h2>

        <div className="mt-12 grid gap-12 lg:grid-cols-[1fr_0.9fr] lg:items-start" data-id="element-527" data-mp="1y6vidxfyjjqo">
          <div data-id="element-528" data-mp="1bzcixmq9amh">
            <p className="max-w-xl text-lg leading-relaxed text-white/80" data-id="element-529" data-mp="1ciolxpp54sl">
              Brand identity is what Morvix is built to be known for. A logo is one asset. An
              identity is the complete visual argument for why a brand deserves attention — built to
              stay consistent, recognisable and memorable everywhere it appears.
            </p>

            <ul className="mt-10 border-t border-white/20" data-id="element-530" data-mp="1gwrnj2214ph8">
              {pillars.map((pillar, index) => <Reveal as="li" key={pillar.title} delay={index * 0.08} data-id="element-531" data-mp="1lw4jccavp6vy" data-mpi={index}>
                  <div className="grid gap-2 border-b border-white/20 py-5 sm:grid-cols-[10rem_1fr] sm:gap-6" data-id="element-532" data-mp="29k4ahdc3qzb2">
                    <span className="label text-lav-soft" data-id="element-533" data-mp="2ek0gfi4d10t9">
                      0{index + 1} — {pillar.title}
                    </span>
                    <p className="text-sm leading-relaxed text-white/75" data-id="element-534" data-mp="1zm822tajuign">{pillar.copy}</p>
                  </div>
                </Reveal>)}
            </ul>

            <div className="mt-9" data-id="element-535" data-mp="o69kg11b2o3t">
              <MagneticButton to="/services" variant="outlineLight" cursorLabel="Identity" showArrow data-id="element-536" data-mp="o9fapjlo7w14">
                Explore Identity Design
              </MagneticButton>
            </div>
          </div>

          <Reveal data-id="element-537" data-mp="32ekcakq4lfe2">
            <figure className="relative" data-id="element-538" data-mp="32h6jgj93tsvr">
              <img src={images.guidelines} alt="Brand guidelines spread showing typographic system and colour swatches" className="w-full object-cover" loading="lazy" decoding="async" data-id="element-539" data-mp="3gf4sd94nox3v" />
              <figcaption className="label mt-3 text-white/60" data-id="element-540" data-mp="2jouhhxmd4tm5">
                Identity system — guidelines & applications
              </figcaption>
            </figure>
          </Reveal>
        </div>

        {/* Supplied client case-study visual — full-width editorial reveal */}
        <figure className="mt-16 sm:mt-20" data-id="element-541" data-mp="2snfxpm4d3wjr">
          <figcaption className="mb-5 flex flex-col gap-2 border-t border-white/20 pt-5 sm:flex-row sm:items-baseline sm:justify-between" data-id="element-542" data-mp="24ymwba4z0whr">
            <span className="font-display text-xl font-bold uppercase tracking-tight sm:text-2xl" data-id="element-543" data-mp="20w09jnn5q1rf">
              Wayne &amp; Co — Identity System
            </span>
            <span className="label text-white/60" data-id="element-544" data-mp="2in6roc31mycx">
              Logo suite · Colour · Typography · Brand applications
            </span>
          </figcaption>
          <motion.div className="overflow-hidden border border-white/15 bg-white/5" initial={reduced ? {
          opacity: 1
        } : {
          clipPath: 'inset(0 0 100% 0)'
        }} whileInView={{
          clipPath: 'inset(0 0 0% 0)',
          opacity: 1
        }} viewport={{
          once: true,
          margin: '-10% 0px'
        }} transition={{
          duration: 1.2,
          ease: [0.76, 0, 0.24, 1]
        }} data-id="element-545" data-mp="2sbyey6x5suhi">
            <motion.img src={images.brandIdentity} alt="Wayne & Co brand identity system in progress — logo suite, colour palette, Helvetica Neue typography, brand guideline layouts and social applications laid out on screen" className="block h-auto w-full" initial={reduced ? undefined : {
            scale: 1.06
          }} whileInView={{
            scale: 1
          }} viewport={{
            once: true,
            margin: '-10% 0px'
          }} transition={{
            duration: 1.6,
            ease: [0.16, 1, 0.3, 1]
          }} loading="lazy" decoding="async" data-id="element-546" data-mp="3bb2kh5phs64f" />
          </motion.div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/70" data-id="element-547" data-mp="upffvtifrzk9">
            A full identity build for Wayne &amp; Co — monogram construction, colour system,
            typographic hierarchy and the guideline document that keeps every application
            consistent.
          </p>
        </figure>
      </div>
    </section>;
}